<template>
    <v-container class="mt-3">
        <!-- Error banner -->
        <v-card v-if="errors.length" class="mb-3">
            <v-card-text>
                <v-alert class="bg-error" closable close-label="Close Alert" density="compact"
                         variant="tonal">
                    <p v-for="(error, idx) in errors" :key="idx">{{ error }}</p>
                </v-alert>
            </v-card-text>
        </v-card>

        <!-- Completion screen -->
        <div v-if="showingCompletion" class="d-flex justify-center align-center" style="min-height: 60vh;">
            <v-card max-width="500" class="text-center pa-8" elevation="2" rounded="lg">
                <v-icon color="success" size="64" class="mb-4">mdi-check-circle</v-icon>
                <h4 class="text-h5 font-weight-bold mb-2">All Set!</h4>
                <p class="text-body-1 text-grey-darken-1 mb-6">You've accepted all required documents.</p>
                <v-btn color="success" size="large" @click="goToDashboard">
                    Continue to Dashboard
                    <v-icon end>mdi-arrow-right</v-icon>
                </v-btn>
            </v-card>
        </div>

        <!-- Welcome card -->
        <div v-else-if="showingWelcome && currentDoc" class="d-flex justify-center align-center" style="min-height: 60vh;">
            <v-card max-width="500" class="text-center pa-8" elevation="2" rounded="lg">
                <v-avatar :color="docIconColor" size="64" class="mb-4">
                    <v-icon size="32" color="white">{{ docIcon }}</v-icon>
                </v-avatar>

                <h4 class="text-h5 font-weight-bold mb-1">{{ currentDoc.title }}</h4>
                <p v-if="docSubtitle" class="text-body-2 text-grey-darken-1 mb-3">{{ docSubtitle }}</p>

                <p v-if="currentDoc.description" class="text-body-1 text-grey-darken-2 mb-4" style="line-height: 1.6;">
                    {{ currentDoc.description }}
                </p>

                <!-- Progress (multi-doc) -->
                <div v-if="totalDocs > 1" class="mb-5">
                    <p class="text-body-2 text-grey-darken-1 mb-2">Document {{ currentDocIndex + 1 }} of {{ totalDocs }}</p>
                    <div class="d-flex justify-center ga-2">
                        <div v-for="i in totalDocs" :key="i"
                             :style="{
                                 width: '10px', height: '10px', borderRadius: '50%',
                                 backgroundColor: i <= currentDocIndex + 1 ? '#6B46C1' : undefined,
                                 border: i > currentDocIndex + 1 ? '2px solid #BDBDBD' : undefined
                             }"
                        />
                    </div>
                </div>

                <v-btn color="primary" size="large" block @click="showingWelcome = false; startTimer()">
                    Review Document
                    <v-icon end>mdi-arrow-right</v-icon>
                </v-btn>

                <v-btn variant="text" class="mt-3" color="grey" @click="$emit('logout')">Logout</v-btn>
            </v-card>
        </div>

        <!-- PDF viewer -->
        <template v-else>
            <v-card class="mb-2">
                <v-card-title class="d-flex justify-space-between align-center">
                    <div>
                        <span v-if="totalDocs > 1" class="text-body-2 text-grey-darken-1">
                            Document {{ currentDocIndex + 1 }} of {{ totalDocs }}
                        </span>
                    </div>
                    <v-btn variant="outlined" size="small" @click="$emit('logout')">Logout</v-btn>
                </v-card-title>
            </v-card>
            <v-row>
                <v-col cols="12">
                    <HtmlDocViewer
                        v-if="currentDoc?.doc && currentDoc?.format === 'html'"
                        :key="currentDoc?.id"
                        :src="currentDoc.doc"
                        :title="`${currentDoc.title} - ${currentDoc.version}`"
                        height="70vh"
                        @sign-action="signDocument"
                    />
                    <PDFCanvasViewer
                        v-else-if="currentDoc?.doc"
                        :key="currentDoc?.id"
                        :scale="1.25"
                        :src="currentDoc.doc"
                        :title="`${currentDoc.title} - ${currentDoc.version}`"
                        height="70vh"
                        @sign-action="signDocument"
                    />
                    <div v-else-if="pdfError" class="text-center pa-8">
                        <v-icon color="error" size="48">mdi-file-alert-outline</v-icon>
                        <p class="mt-4 text-body-1">Failed to load document</p>
                        <v-btn class="mt-4" color="primary" variant="outlined" @click="retryDocument">Retry</v-btn>
                    </div>
                    <div v-else class="d-flex justify-center align-center pa-8">
                        <v-progress-circular indeterminate color="primary"/>
                        <span class="ml-3 text-body-2">Preparing your document...</span>
                    </div>
                </v-col>
            </v-row>
        </template>

        <!-- Decline confirmation dialog -->
        <v-dialog v-model="showDeclineDialog" max-width="440" persistent>
            <v-card class="pa-4">
                <v-card-title class="text-h6">Are you sure?</v-card-title>
                <v-card-text>
                    Declining {{ currentDoc?.title ? `"${currentDoc.title}"` : 'this document' }}
                    will prevent access to your account. You can accept later by signing in again.
                </v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn variant="outlined" @click="showDeclineDialog = false">Go Back</v-btn>
                    <v-btn color="error" @click="confirmDecline">Decline &amp; Log Out</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup>
import PDFCanvasViewer from '@/components/PdfViewer.vue';
import HtmlDocViewer from '@/components/HtmlDocViewer.vue';
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useLoadingStore} from "@/stores/loadingStore";
import getDeviceName from '@/assets/js/device-info';
import {send} from "@/composables/apiClient";
import {onMounted, onBeforeUnmount, ref, computed} from "vue";
import router from "@/router";
import {useAuthStore} from "@/stores/authStore";
import {landingRoute} from "@/composables/globals";


const props = defineProps(['docs']);

const loadingStore = useLoadingStore();
const {showSnackbar} = useSnackbarStore();
const docs = ref(props.docs ?? []);
const initialDocCount = props.docs?.length ?? 0;
const deviceDetails = ref(null);
const currentDocId = ref(null);
const currentDoc = ref(null);
const ipInfo = ref(null);
const errors = ref([]);
const pdfError = ref(false);
const showingWelcome = ref(true);
const showingCompletion = ref(false);
const showDeclineDialog = ref(false);
let completionTimer = null;

const emits = defineEmits(['updateConsents', 'logout']);

const totalDocs = initialDocCount;
const currentDocIndex = computed(() => totalDocs - docs.value.length);

const docIcon = computed(() => {
    const key = (currentDoc.value?.doc_key ?? '').toLowerCase();
    if (key.includes('terms') || key.includes('tos')) return 'mdi-gavel';
    if (key.includes('privacy')) return 'mdi-lock-outline';
    if (key.includes('eula')) return 'mdi-file-sign';
    return 'mdi-file-document-outline';
});

const docIconColor = computed(() => {
    const key = (currentDoc.value?.doc_key ?? '').toLowerCase();
    if (key.includes('terms') || key.includes('tos')) return '#6B46C1';
    if (key.includes('privacy')) return '#0284C7';
    if (key.includes('eula')) return '#059669';
    return '#6B46C1';
});

const docSubtitle = computed(() => {
    const parts = [];
    if (currentDoc.value?.version) parts.push(`Version ${currentDoc.value.version}`);
    if (currentDoc.value?.effective_date) {
        try {
            const d = new Date(currentDoc.value.effective_date);
            const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            parts.push(`Effective ${months[d.getMonth()]} ${d.getFullYear()}`);
        } catch { /* ignore */ }
    }
    return parts.join(' · ');
});

const startTimer = () => {
    // Reset time tracking when user enters PDF
};

onMounted(async () => {
    await getDocument();
    await getIpGeo();
    deviceDetails.value = getDeviceName()
})
const getDocument = async () => {
    if (!docs.value?.length) {
        // All done - show completion
        showingCompletion.value = true;
        showingWelcome.value = false;
        emits('updateConsents', null);
        completionTimer = setTimeout(() => {
            router.push({name: landingRoute(useAuthStore())});
        }, 3000);
        return;
    }
    pdfError.value = false;
    showingWelcome.value = true;
    currentDocId.value = docs.value[0] ?? null;
    if (currentDocId.value) {
        try {
            const response = await send('get', `legal-document-details/${currentDocId.value}`);
            if (response.data.success) {
                currentDoc.value = response.data.data;
                await fetchDocumentFile(currentDoc.value.url);
            } else {
                pdfError.value = true;
                showSnackbar('Unable to load document', 'error');
            }
        } catch (e) {
            console.log(e);
            pdfError.value = true;
            showSnackbar('Failed to fetch document', 'error');
        }
    }
};
const retryDocument = () => {
    pdfError.value = false;
    if (currentDoc.value?.doc) URL.revokeObjectURL(currentDoc.value.doc);
    currentDoc.value = null;
    getDocument();
};
const fetchDocumentFile = async (pdfUrl) => {
    try {
        const response = await send('get', pdfUrl, null, {
            responseType: 'blob',
        });
        if (response.status === 200 && response.data?.size > 0) {
            // format comes from the details response. Never sniff the URL: it is
            // a 30 second signed route with no extension on it.
            const mime = currentDoc.value?.format === 'html'
                ? 'text/html'
                : 'application/pdf';
            const docBlob = new Blob([response.data], {type: mime});
            currentDoc.value.doc = window.URL.createObjectURL(docBlob);
        } else {
            pdfError.value = true;
            showSnackbar('Failed to download document', 'error');
        }
    } catch (e) {
        console.log(e);
        pdfError.value = true;
        showSnackbar('Failed to download document', 'error');
    }
}
const getIpGeo = async () => {
    try {
        const res = await fetch('https://free.freeipapi.com/api/json');
        ipInfo.value = await res.json();
    } catch {
        ipInfo.value = null;
    }
};

const confirmDecline = async () => {
    showDeclineDialog.value = false;
    await signDocument({accept: 0, scrolled_to_bottom: false, time_spent: 0}, true);
    emits('logout');
};

const signDocument = async (feedback, skipDeclineCheck = false) => {
    // Intercept decline - show confirmation dialog
    if (feedback.accept === 0 && !skipDeclineCheck) {
        showDeclineDialog.value = true;
        return;
    }

    errors.value = [];
    loadingStore.showLoading('Processing...', 'Recording your response. Please wait.');
    const data = {...feedback, device_details: deviceDetails.value, ip_info: ipInfo.value}
    try {
        const response = await send('post', `legal-document-sign/${currentDocId.value}`, data);
        if (response.data.success) {
            const remaining = response.data.data;
            docs.value = remaining;
            emits('updateConsents', remaining);
            if (currentDoc.value?.doc) URL.revokeObjectURL(currentDoc.value.doc);
            currentDoc.value = null;
            await getDocument();
        }
    } catch (errorResponse) {
        console.log(errorResponse);
        if (errorResponse.response?.data?.errors) {
            let validationErrors = errorResponse.response.data.errors;
            Object.keys(validationErrors).forEach((key) => {
                errors.value.push(validationErrors[key][0]);
            });
        } else if (errorResponse.response?.data?.message) {
            errors.value.push(errorResponse.response.data.message);
        }
        showSnackbar('Something went wrong', 'error');
    } finally {
        loadingStore.hideLoading();
    }
}

const goToDashboard = () => {
    if (completionTimer) clearTimeout(completionTimer);
    router.push({name: landingRoute(useAuthStore())});
};

onBeforeUnmount(() => {
    if (currentDoc.value?.doc) {
        URL.revokeObjectURL(currentDoc.value.doc);
    }
    if (completionTimer) clearTimeout(completionTimer);
});

</script>
